import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowRight, Copy, Check, Loader2, RotateCcw, ChevronDown, ChevronRight,
    PartyPopper, ListChecks, BarChart3, Lightbulb, AlertTriangle, Target,
    Wand2,
} from "lucide-react";
import OnboardingShell from "./OnboardingShell";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import LaunchWizard, { computeChecklistFromWizard } from "./LaunchWizard";
import {
    getGroupAds, getAdHeadlines, getAdDescriptions, getHeadlinePins,
    getHeadlineParts, countPinned, lengthStatus, adAsPlainText,
    getGroupKeywords, getGroupNegatives, getGroupKeywordsDetailed, getMatchTypeCounts,
    LIMIT_HEADLINE, LIMIT_DESCRIPTION, RSA_MAX_HEADLINES,
} from "@/lib/adAssets";

// Real-progress stages reported by the backend in campaigns.stage.
// Each stage has a [floor, ceil] progress range; we animate within that
// range based on time-in-stage so the bar moves smoothly even when the
// backend doesn't update for a while. Hitting the next stage jumps us to
// its floor — never backwards. The total expected time per stage is used
// for the within-stage easing.
const STAGES = {
    // ----- LEGACY single-prompt pipeline -----
    scraping:            { floor: 0,   ceil: 14,  expectedMs: 25_000,
                           text: "Аналізуємо сайт і конкурентів..." },
    generating_strategy: { floor: 15,  ceil: 87,  expectedMs: 150_000,
                           text: "AI будує стратегію, ключові слова і тексти оголошень..." },
    // Backend sets this stage when Anthropic returns 429 (rate-limit
    // exceeded) and we are sleeping before retry. We hold the bar at the
    // generating_strategy floor so it doesn't snap backwards, and show a
    // dedicated message so the user understands what's happening.
    waiting_rate_limit:  { floor: 15,  ceil: 16,  expectedMs: 60_000,
                           text: "AI зараз перевантажений, чекаємо вільного слоту..." },

    // ----- NEW multi-stage chain pipeline -----
    // Backend (MULTI_STAGE_CHAIN=true) writes these in sequence. Each one
    // owns a distinct slice of the bar so the user sees real progress.
    stage1_strategist:   { floor: 10,  ceil: 28,  expectedMs: 60_000,
                           text: "Етап 1/4: AI-стратег аналізує бізнес та визначає ICP..." },
    stage2_keywords:     { floor: 30,  ceil: 53,  expectedMs: 80_000,
                           text: "Етап 2/4: Підбираємо ключові слова та мінус-слова..." },
    stage3_architect:    { floor: 55,  ceil: 78,  expectedMs: 145_000,
                           text: "Етап 3/4: Будуємо архітектуру кампаній та бюджети..." },
    stage4_copy:         { floor: 80,  ceil: 94,  expectedMs: 100_000,
                           text: "Етап 4/4: Пишемо тексти оголошень та розширення..." },

    // ----- Shared terminal states -----
    parsing:             { floor: 95,  ceil: 99,  expectedMs: 2_000,
                           text: "Перевіряємо валідність стратегії..." },
    ready:               { floor: 100, ceil: 100, expectedMs: 0,
                           text: "Готово!" },
};
// Default when backend hasn't yet written a stage (first ~500 ms of the job).
const DEFAULT_STAGE = "scraping";

const easeOut = (t) => 1 - Math.pow(1 - t, 3);

// Compute the displayed progress percentage for a given stage + elapsed-in-stage.
// `previousFloor` lets us recover gracefully if the backend skipped a stage update
// (we never want the bar to go backwards).
const computeProgress = (stageKey, msInStage, previousFloor = 0) => {
    const s = STAGES[stageKey] || STAGES[DEFAULT_STAGE];
    const t = s.expectedMs > 0 ? Math.min(1, msInStage / s.expectedMs) : 1;
    const within = s.floor + (s.ceil - s.floor) * easeOut(t);
    return Math.max(previousFloor, within);
};

// Persist the moment we kicked off the AI generation so that a page reload
// during the 2-3 min wait doesn't visually reset the progress bar to 0.
// Cleared when the strategy is ready/errored. Per-user keyed so multiple
// accounts on the same browser don't trample each other.
const STARTED_AT_KEY = (userId) => `autoads_strategy_started_${userId || "anon"}`;
// If a stored start timestamp is older than this, assume it's stale (server
// restart killed the job, user closed the tab yesterday, etc.) and start fresh.
const STARTED_AT_MAX_AGE_MS = 15 * 60 * 1000;

export default function Step3Strategy() {
    const navigate = useNavigate();
    const { user, refresh } = useAuth();
    const [strategy, setStrategy] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [progress, setProgress] = useState(0);
    const [tab, setTab] = useState("strategy"); // strategy | wizard | checklist
    const [launched, setLaunched] = useState(false);
    const [wizardState, setWizardState] = useState(null);
    const [bizInfo, setBizInfo] = useState(null);
    const [elapsedMs, setElapsedMs] = useState(0);
    const calledRef = useRef(false);
    const pollRef = useRef(null);
    const progressRef = useRef(null);
    const startedAtRef = useRef(null);
    const stageRef = useRef(DEFAULT_STAGE);
    const stageStartedAtRef = useRef(null);
    const [stage, setStage] = useState(DEFAULT_STAGE);
    const readyRef = useRef(false);

    const currentText = (STAGES[stage] || STAGES[DEFAULT_STAGE]).text;

    // text comes from the current backend stage, not from progress %

    useEffect(() => {
        progressRef.current = setInterval(() => {
            if (readyRef.current) return;
            const stage = stageRef.current || DEFAULT_STAGE;
            const stageStarted = stageStartedAtRef.current || startedAtRef.current || Date.now();
            const msInStage = Date.now() - stageStarted;
            // Pass the highest progress we've reached so the bar can never go backwards
            // (e.g. if the backend stage write was delayed and we recompute mid-stage).
            setProgress(prev => computeProgress(stage, msInStage, prev));
            setElapsedMs(Date.now() - (startedAtRef.current || Date.now()));
        }, 200);
        return () => { if (progressRef.current) clearInterval(progressRef.current); };
    }, []);

    // Sync tab to URL hash so refreshes preserve position.
    useEffect(() => {
        const hash = window.location.hash.replace("#", "");
        if (hash === "wizard" || hash === "checklist" || hash === "strategy") setTab(hash);
        else if (hash === "launch") setTab("wizard"); // legacy URL
    }, []);
    useEffect(() => {
        if (loading) return;
        window.history.replaceState(null, "", `#${tab}`);
    }, [tab, loading]);

    const poll = async () => {
        try {
            const r = await api.get("/strategy");
            const doc = r.data;
            // Sync real backend stage. Backend writes stage transitions
            // (scraping → generating_strategy → parsing → ready).
            // stage_started_at lets us animate within the stage with accurate timing
            // even if the backend's clock differs slightly from ours.
            if (doc?.stage && doc.stage !== stageRef.current && STAGES[doc.stage]) {
                stageRef.current = doc.stage;
                const ts = doc.stage_started_at ? Date.parse(doc.stage_started_at) : Date.now();
                stageStartedAtRef.current = isNaN(ts) ? Date.now() : ts;
                setStage(doc.stage);
            }
            if (doc?.status === "ready" && doc?.strategy_json) {
                readyRef.current = true;
                setProgress(100);
                try { localStorage.removeItem(STARTED_AT_KEY(user?.user_id)); } catch {}
                const enriched = {
                    ...doc.strategy_json,
                    _site_analysis: doc.site_analysis || null,
                    _competitor_count: Array.isArray(doc.competitor_landscape) ? doc.competitor_landscape.length : 0,
                };
                setTimeout(() => {
                    setStrategy(enriched);
                    setLoading(false);
                }, 400);
                return true;
            }
            if (doc?.status === "error") {
                readyRef.current = true;
                try { localStorage.removeItem(STARTED_AT_KEY(user?.user_id)); } catch {}
                setError(doc.error || "AI повернув помилку");
                setLoading(false);
                return true;
            }
        } catch {}
        return false;
    };

    const load = async () => {
        setLoading(true);
        setError("");
        setProgress(0);
        setElapsedMs(0);
        stageRef.current = DEFAULT_STAGE;
        stageStartedAtRef.current = null;
        setStage(DEFAULT_STAGE);
        readyRef.current = false;
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }

        const cached = await api.get("/strategy").catch(() => ({ data: null }));
        if (cached.data?.status === "ready" && cached.data?.strategy_json) {
            // Strategy is ready — clear any stale "in progress" timestamp.
            try { localStorage.removeItem(STARTED_AT_KEY(user?.user_id)); } catch {}
            const enriched = {
                ...cached.data.strategy_json,
                _site_analysis: cached.data.site_analysis || null,
                _competitor_count: Array.isArray(cached.data.competitor_landscape) ? cached.data.competitor_landscape.length : 0,
            };
            setStrategy(enriched);
            setLoading(false);
            return;
        }

        // Resume an in-flight generation on reload: if backend already has
        // a "generating" doc AND we have a recent persisted start timestamp,
        // pick up where we left off. Otherwise start the clock fresh.
        let startedAt = Date.now();
        if (cached.data?.status === "generating") {
            try {
                const saved = parseInt(localStorage.getItem(STARTED_AT_KEY(user?.user_id)) || "0", 10);
                if (saved && Date.now() - saved < STARTED_AT_MAX_AGE_MS) {
                    startedAt = saved;
                }
            } catch {}
        }
        startedAtRef.current = startedAt;
        try { localStorage.setItem(STARTED_AT_KEY(user?.user_id), String(startedAt)); } catch {}

        try {
            // Backend /strategy/generate is idempotent — if a doc with
            // status="generating" exists, it returns it without spawning a
            // duplicate Sonnet call. Safe to call on every load()/reload.
            await api.post("/strategy/generate");
        } catch (e) {
            try { localStorage.removeItem(STARTED_AT_KEY(user?.user_id)); } catch {}
            setError(e?.response?.data?.detail || "Не вдалося стартувати генерацію");
            setLoading(false);
            return;
        }

        let attempts = 0;
        pollRef.current = setInterval(async () => {
            attempts++;
            const done = await poll();
            if (done) { clearInterval(pollRef.current); pollRef.current = null; }
            // No hard client-side timeout — backend (require_active_plan +
            // AsyncAnthropic timeout=180s) is the real source of truth. We
            // poll until backend marks the campaigns doc as ready or error.
            // Safety net at 12 minutes for runaway cases (e.g. Railway killed
            // the background task without setting error state).
            else if (attempts > 240) {
                clearInterval(pollRef.current); pollRef.current = null;
                setError("AI не відповів вчасно. Спробуйте ще раз.");
                setLoading(false);
            }
        }, 3000);
    };

    // Source of truth for paid status: stripe_customer_id (set by Stripe webhook
    // or polling activation). user.plan is "starter" for everyone via auto-bootstrap
    // and is NOT a reliable paid signal — backend require_active_plan agrees.
    const hasPaid = Boolean(user?.stripe_customer_id);

    useEffect(() => {
        if (calledRef.current) return;
        // PR: delayed-paywall. Skip auto-generation if the user hasn't paid.
        // Instead, render the paywall card (handled below via the early-return).
        // Once they pay and come back via CheckoutSuccess, pending_onboarding_action
        // triggers a programmatic re-mount which DOES have stripe_customer_id → load() runs.
        if (!hasPaid) {
            setLoading(false);
            return;
        }
        calledRef.current = true;
        load();
        // Best-effort fetch business profile for wizard prefill.
        api.get("/business").then(r => setBizInfo(r.data || {})).catch(() => {});
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasPaid]);

    // PR: delayed-paywall. Step 3 is where the AI cost begins, so this is
    // where we ask for payment. Show a paywall card BEFORE we ever call
    // /strategy/generate. Backend enforces the same rule (require_active_plan)
    // so a curl bypass returns 402.
    if (!hasPaid) {
        return <Step3Paywall navigate={navigate} />;
    }

    if (loading) {
        return (
            <OnboardingShell current={3}>
                <div className="max-w-[640px] mx-auto py-16" data-testid="onb3-loading">
                    <div className="flex items-center justify-center gap-3">
                        <Loader2 size={20} className="animate-spin text-primary-c" />
                        <h2 className="font-display font-bold text-2xl text-text-primary">Генеруємо стратегію</h2>
                    </div>

                    <div className="mt-8 h-2 w-full bg-surface rounded-full overflow-hidden border border-border" data-testid="onb3-progress-bar">
                        <div
                            className="h-full transition-[width] duration-300 ease-out"
                            style={{
                                width: `${progress}%`,
                                background: "linear-gradient(90deg, #6D5EF8 0%, #2DD4BF 100%)",
                                boxShadow: "0 0 12px rgba(109,94,248,0.4)",
                            }}
                        />
                    </div>
                    <div className="mt-2 flex items-center justify-between font-mono text-xs">
                        <span className="text-secondary-c">{currentText}</span>
                        <span className="text-primary-c tabular-nums" data-testid="onb3-progress-pct">{Math.floor(progress)}%</span>
                    </div>

                    {/* Real-stage milestone blocks. Each block is tied to an actual
                        backend phase (scraping / generating_strategy / parsing / ready)
                        rather than a fictional time-based threshold. */}
                    <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-2">
                        {[
                            { label: "Аналіз сайту",      doneAt: 14, activeFrom: 0 },
                            { label: "Конкуренти",        doneAt: 14, activeFrom: 0 },
                            { label: "Будуємо стратегію", doneAt: 87, activeFrom: 15 },
                            { label: "Фіналізація",       doneAt: 97, activeFrom: 88 },
                        ].map((m, i) => {
                            const done = progress >= m.doneAt;
                            const active = !done && progress >= m.activeFrom;
                            return (
                                <div key={i} className={`text-[10px] font-mono uppercase tracking-wider px-2 py-2 rounded-md border text-center transition ${
                                    done ? "border-success/40 text-success-c bg-success/5"
                                      : active ? "border-primary/40 text-primary-c bg-primary/10"
                                      : "border-border text-faint-c"
                                }`}>
                                    {done ? "✓ " : active ? "▸ " : "○ "}
                                    {m.label}
                                </div>
                            );
                        })}
                    </div>

                    {/* Reassurance banner — appears after 2 min when users typically
                        start to worry. Backend can legitimately take up to ~4 min for
                        large business profiles + many keywords. */}
                    {elapsedMs > 120_000 && (
                        <div
                            className="mt-6 mx-auto max-w-[480px] rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 animate-in fade-in slide-in-from-bottom-2 duration-500"
                            data-testid="onb3-reassurance"
                        >
                            <p className="text-center text-xs text-primary-c leading-relaxed">
                                ✨ Усе йде за планом — AI ретельно опрацьовує деталі вашого бізнесу.
                                {elapsedMs > 180_000
                                    ? " Складні стратегії іноді займають до 4 хвилин. Будь ласка, не закривайте сторінку."
                                    : " Зачекайте ще трохи, будь ласка."}
                            </p>
                        </div>
                    )}
                </div>
            </OnboardingShell>
        );
    }

    if (error || !strategy) {
        return (
            <OnboardingShell current={3}>
                <div className="max-w-[560px] mx-auto text-center py-20" data-testid="onb3-error">
                    <h2 className="font-display font-bold text-2xl text-text-primary">Щось пішло не так</h2>
                    <p className="mt-3 text-secondary-c">{error || "AI повернув неочікувану відповідь"}</p>
                    <button onClick={load} data-testid="onb3-retry" className="btn-primary mt-6">
                        <RotateCcw size={16} /> Спробувати знову
                    </button>
                </div>
            </OnboardingShell>
        );
    }

    if (launched) {
        return (
            <OnboardingShell current={3}>
                <div className="max-w-[520px] mx-auto text-center py-20" data-testid="onb-launched">
                    <PartyPopper size={48} className="text-success-c mx-auto" />
                    <h2 className="mt-6 font-display font-extrabold text-3xl text-text-primary">Реклама запущена!</h2>
                    <p className="mt-3 text-secondary-c">Перші результати через 3-7 днів. Переходимо на дашборд…</p>
                </div>
            </OnboardingShell>
        );
    }

    const budget = strategy.budget || strategy.budget_analysis || {};
    const campaigns = strategy.campaigns || (strategy.campaign ? [strategy.campaign] : []);
    const warnings = strategy.warnings || [];
    const recommendations = strategy.additional_recommendations || [];
    const siteAnalysis = strategy._site_analysis;
    const competitorCount = strategy._competitor_count || 0;

    return (
        <OnboardingShell current={3}>
            <div className="max-w-[1100px] mx-auto" data-testid="step3-root">
                {/* Header */}
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <h1 className="font-display font-extrabold text-text-primary" style={{ fontSize: "clamp(28px, 4vw, 44px)", letterSpacing: "-0.02em" }}>
                        Ваша стратегія готова
                    </h1>
                    {strategy.language && (
                        <span className="text-xs font-mono text-faint-c uppercase">
                            // мова ринку: {strategy.language}{strategy.market ? ` · ${strategy.market}` : ""}
                        </span>
                    )}
                </div>
                <p className="mt-3 text-secondary-c">
                    Перегляньте стратегію та коли будете готові — перейдіть до чекліста запуску.
                </p>

                {(siteAnalysis || competitorCount > 0) && (
                    <div className="mt-5 flex flex-wrap gap-2" data-testid="strategy-scrape-badges">
                        {siteAnalysis && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-mono border border-success/30 bg-success/10 text-success-c">
                                ✓ Сайт проаналізовано
                            </span>
                        )}
                        {competitorCount > 0 && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-mono border border-success/30 bg-success/10 text-success-c">
                                ✓ Конкурентів проаналізовано: {competitorCount}
                            </span>
                        )}
                    </div>
                )}

                {/* Tabs */}
                <div
                    className="mt-7 sticky top-0 z-20 -mx-4 px-4 py-3 backdrop-blur-md bg-background/80 border-b border-border"
                    data-testid="step3-tabs"
                >
                    <div className="flex items-center gap-2 flex-wrap">
                        <TabButton
                            active={tab === "strategy"}
                            onClick={() => setTab("strategy")}
                            icon={<BarChart3 size={15} />}
                            label="Стратегія"
                            testid="tab-strategy"
                        />
                        <TabButton
                            active={tab === "wizard"}
                            onClick={() => setTab("wizard")}
                            icon={<Wand2 size={15} />}
                            label="Запуск кампанії"
                            testid="tab-wizard"
                        />
                        <TabButton
                            active={tab === "checklist"}
                            onClick={() => setTab("checklist")}
                            icon={<ListChecks size={15} />}
                            label="Фінальний чекліст"
                            testid="tab-checklist"
                        />
                    </div>
                </div>

                {tab === "strategy" && (
                    <StrategyView
                        budget={budget}
                        campaigns={campaigns}
                        warnings={warnings}
                        recommendations={recommendations}
                        onGoLaunch={() => { setTab("wizard"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    />
                )}

                {tab === "wizard" && (
                    <LaunchWizard
                        userId={user?.user_id}
                        strategy={strategy}
                        bizInfo={bizInfo}
                        onProgressChange={setWizardState}
                        onSkip={() => { setTab("checklist"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                        onFinish={() => { setTab("checklist"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    />
                )}

                {tab === "checklist" && (
                    <ChecklistView
                        userId={user?.user_id}
                        strategy={strategy}
                        wizardState={wizardState}
                        onLaunched={async () => {
                            await api.post("/campaign/launch");
                            await refresh();
                            setLaunched(true);
                            setTimeout(() => navigate("/dashboard"), 2200);
                        }}
                        onBack={() => { setTab("wizard"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    />
                )}
            </div>
        </OnboardingShell>
    );
}

const TabButton = ({ active, onClick, icon, label, testid }) => (
    <button
        onClick={onClick}
        data-testid={testid}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition ${
            active
                ? "bg-primary/15 text-primary-c border border-primary/30"
                : "border border-border text-secondary-c hover:text-text-primary hover:border-primary/30"
        }`}
    >
        {icon} {label}
    </button>
);

/* ─────────────────── STRATEGY VIEW ─────────────────── */
export const StrategyView = ({ budget, campaigns, warnings, recommendations, onGoLaunch }) => {
    const sections = useMemo(() => [
        { id: "sec-budget", label: "Бюджет", icon: <BarChart3 size={13} /> },
        { id: "sec-campaigns", label: `Кампанії (${campaigns.length})`, icon: <Target size={13} /> },
        warnings.length > 0 ? { id: "sec-warnings", label: "Попередження", icon: <AlertTriangle size={13} /> } : null,
        recommendations.length > 0 ? { id: "sec-recs", label: "Рекомендації", icon: <Lightbulb size={13} /> } : null,
    ].filter(Boolean), [campaigns.length, warnings.length, recommendations.length]);

    const jump = (id) => {
        const el = document.getElementById(id);
        if (el) {
            const y = el.getBoundingClientRect().top + window.pageYOffset - 100;
            window.scrollTo({ top: y, behavior: "smooth" });
        }
    };

    return (
        <>
            {/* Section anchors */}
            <nav
                className="mt-5 flex flex-wrap items-center gap-1.5 text-xs"
                data-testid="strategy-toc"
            >
                <span className="text-faint-c font-mono mr-1">До розділу:</span>
                {sections.map((s) => (
                    <button
                        key={s.id}
                        onClick={() => jump(s.id)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border text-secondary-c hover:text-text-primary hover:border-primary/40 transition"
                        data-testid={`toc-${s.id}`}
                    >
                        {s.icon} {s.label}
                    </button>
                ))}
            </nav>

            <div id="sec-budget"><BudgetCard data={budget} /></div>

            {warnings.length > 0 && <div id="sec-warnings"><WarningsCard items={warnings} /></div>}

            <div id="sec-campaigns" className="space-y-5 mt-6">
                {campaigns.map((c, i) => <CampaignCard key={i} data={c} idx={i} defaultOpen={i === 0} />)}
            </div>

            {recommendations.length > 0 && <div id="sec-recs"><RecommendationsCard items={recommendations} /></div>}

            <div className="mt-10 mb-4 flex justify-end">
                <button onClick={onGoLaunch} className="btn-primary pulse-glow" data-testid="goto-wizard-btn">
                    Перейти до wizard'а запуску <ArrowRight size={16} />
                </button>
            </div>
        </>
    );
};

/* ─────────────────── CHECKLIST VIEW ─────────────────── */
const ChecklistView = ({ userId, strategy, wizardState, onLaunched, onBack }) => {
    // The wizard walks through ONE campaign at a time. Reflect that in counts —
    // don't sum across all campaigns (causes the "5 groups" confusion when user
    // actually generated 3 in their wizard campaign + 2 in an aux campaign).
    const wizardCampIdx = wizardState?.currentCampaignIdx ?? 0;
    const firstCampaign = strategy?.campaigns?.[wizardCampIdx] || strategy?.campaigns?.[0] || strategy?.campaign;
    const budgetBlock = strategy?.budget || strategy?.budget_analysis;
    const dailyBudget = firstCampaign?.daily_budget_usd ?? budgetBlock?.daily_usd ?? budgetBlock?.daily_budget;
    const adGroupsOfCampaign = firstCampaign?.ad_groups || [];
    const totalAdGroups = adGroupsOfCampaign.length;
    const totalKeywords = adGroupsOfCampaign.reduce((a, g) => a + (g.keywords || []).length, 0);

    const items = useMemo(() => [
        { id: "login", label: "Увійшов у Google Ads (Expert Mode)" },
        { id: "campaign", label: `Створив кампанію: ${firstCampaign?.name || "[зі стратегії]"}` },
        { id: "budget", label: `Денний бюджет: $${dailyBudget ?? "—"}` },
        { id: "adgroups", label: `Створив ${totalAdGroups} груп оголошень` },
        { id: "keywords", label: `Додав ${totalKeywords} ключових слів` },
        { id: "negatives", label: "Додав мінус-слова" },
        { id: "ads", label: "Скопіював тексти оголошень (мін. 1 варіант на групу)" },
        { id: "extensions", label: "Додав розширення (callouts, sitelinks)" },
        { id: "active", label: "Статус кампанії: Eligible / Активна" },
    ], [firstCampaign?.name, dailyBudget, totalAdGroups, totalKeywords]);

    const [manualChecks, setManualChecks] = useState({});
    const [submitting, setSubmitting] = useState(false);

    // Auto-ticks from wizard progress (read from localStorage in case state is empty)
    const autoChecks = useMemo(() => {
        let state = wizardState;
        if (!state && userId) {
            try {
                const saved = localStorage.getItem(`autoads_wizard_${userId}`);
                if (saved) state = JSON.parse(saved);
            } catch {}
        }
        return computeChecklistFromWizard(state, strategy);
    }, [wizardState, userId, strategy]);

    // Load manual overrides
    useEffect(() => {
        if (!userId) return;
        try {
            const saved = localStorage.getItem(`autoads_checklist_${userId}`);
            if (saved) setManualChecks(JSON.parse(saved));
        } catch {}
    }, [userId]);

    // Effective: auto OR manual.
    const isChecked = (id) => !!(autoChecks[id] || manualChecks[id]);

    const toggle = (id) => {
        const next = { ...manualChecks, [id]: !manualChecks[id] };
        setManualChecks(next);
        if (userId) localStorage.setItem(`autoads_checklist_${userId}`, JSON.stringify(next));
    };

    const doneCount = items.filter((it) => isChecked(it.id)).length;
    const allChecked = doneCount === items.length;
    const pct = Math.round((doneCount / items.length) * 100);

    const handleLaunch = async () => {
        setSubmitting(true);
        try {
            await onLaunched();
        } catch {
            setSubmitting(false);
        }
    };

    return (
        <div className="mt-6" data-testid="checklist-view">
            {/* Progress summary */}
            <section className="glass rounded-2xl p-6">
                <div className="flex items-center gap-3">
                    <ListChecks size={20} className="text-primary-c" />
                    <h2 className="font-display font-bold text-xl text-text-primary">Фінальний чекліст</h2>
                </div>
                <p className="mt-2 text-sm text-secondary-c">
                    Галочки автоматично проставляються на основі вашого прогресу в wizard'і. Натисніть «Я запустив рекламу», коли все готово.
                </p>

                <div className="mt-5 flex items-center gap-3">
                    <div className="flex-1 h-2 bg-surface rounded-full overflow-hidden border border-border">
                        <div
                            className="h-full transition-[width] duration-500 ease-out"
                            style={{
                                width: `${pct}%`,
                                background: "linear-gradient(90deg, #6D5EF8 0%, #2DD4BF 100%)",
                            }}
                            data-testid="checklist-progress-bar"
                        />
                    </div>
                    <span className="text-xs font-mono text-secondary-c tabular-nums" data-testid="checklist-progress-pct">
                        {doneCount} / {items.length}
                    </span>
                </div>
            </section>

            {/* Checklist */}
            <div className="mt-6 space-y-3">
                {items.map((it) => {
                    const checked = isChecked(it.id);
                    const isAuto = !!autoChecks[it.id];
                    return (
                        <label
                            key={it.id}
                            className={`glass rounded-xl p-4 flex items-center gap-4 cursor-pointer transition ${checked ? "border-success/40 bg-success/5" : ""}`}
                            data-testid={`checklist-${it.id}`}
                        >
                            <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggle(it.id)}
                                className="accent-primary w-5 h-5 shrink-0"
                            />
                            <span className={`flex-1 text-base ${checked ? "text-success-c line-through" : "text-text-primary"}`}>
                                {it.label}
                            </span>
                            {isAuto && (
                                <span className="text-[10px] font-mono uppercase tracking-wider text-success-c shrink-0">
                                    auto
                                </span>
                            )}
                        </label>
                    );
                })}
            </div>

            <div className="mt-8 mb-4 flex items-center justify-between flex-wrap gap-3">
                <button
                    onClick={onBack}
                    className="text-secondary-c hover:text-text-primary text-sm inline-flex items-center gap-2"
                    data-testid="back-to-wizard"
                >
                    <ChevronRight size={14} className="rotate-180" /> Назад до wizard'а
                </button>
                <button
                    onClick={handleLaunch}
                    disabled={!allChecked || submitting}
                    data-testid="launch-btn"
                    className={`btn-primary ${(!allChecked || submitting) ? "opacity-50 cursor-not-allowed" : "pulse-glow"}`}
                >
                    {submitting && <Loader2 size={16} className="animate-spin" />}
                    🚀 Я запустив рекламу
                </button>
            </div>
        </div>
    );
};

/* ─────────────────── REUSABLE COMPONENTS ─────────────────── */
const Copyable = ({ value, testid, label = "Копіювати", iconOnly = false }) => {
    const [copied, setCopied] = useState(false);
    const onClick = async (e) => {
        e?.stopPropagation?.();
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {}
    };
    if (iconOnly) {
        return (
            <button
                onClick={onClick}
                data-testid={testid}
                title={copied ? "Скопійовано" : label}
                aria-label={label}
                className={`inline-flex items-center justify-center w-6 h-6 rounded-md border transition ${copied ? "border-success text-success-c bg-success/10" : "border-border text-faint-c hover:border-primary/40 hover:text-primary-c"}`}
            >
                {copied ? <Check size={11} /> : <Copy size={11} />}
            </button>
        );
    }
    return (
        <button onClick={onClick} data-testid={testid} className={`inline-flex items-center gap-1.5 text-xs font-mono px-2 py-1 rounded-md border transition ${copied ? "border-success text-success-c" : "border-border text-secondary-c hover:border-primary/40 hover:text-text-primary"}`}>
            {copied ? <Check size={12} /> : <Copy size={12} />}
            {copied ? "Скопійовано" : label}
        </button>
    );
};

/* ─── Keyword formatting helpers ──────────────────────────────────────
   AI sometimes returns text already wrapped with [...] / "..." / '...'.
   We strip those wrappers so we don't render [[ремонт]] or ""ремонт"".
   Stripping is done in one pass for both displayed pills and the
   copy-to-clipboard output (which we re-wrap based on match_type). */
const BudgetCard = ({ data }) => {
    if (!data) return null;
    const verdict = data.verdict || data.roi_verdict;
    const verdictNote = data.verdict_note || data.roi_note;
    const monthly = data.monthly_usd ?? data.monthly_budget;
    const daily = data.daily_usd ?? data.daily_budget;
    const cpc = data.estimated_avg_cpc;
    const clicks = data.estimated_clicks_monthly ?? data.estimated_clicks;
    const cpa = data.estimated_cpa;
    const conv = data.estimated_conversions_monthly;

    const color = verdict === "profitable" ? "text-success-c"
        : verdict === "risk" || verdict === "insufficient_budget" ? "text-error-c"
        : "text-warning-c";

    return (
        <section className="mt-6 glass rounded-2xl p-7" data-testid="strategy-budget">
            <h2 className="font-display font-bold text-xl text-text-primary">Бюджет та прогноз</h2>
            <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-4">
                {monthly != null && <Stat label="Місячний" value={`$${monthly}`} />}
                {daily != null && <Stat label="Денний" value={`$${daily}`} />}
                {cpc != null && <Stat label="Сер. CPC" value={`$${cpc}`} />}
                {clicks != null && <Stat label="Кліків/міс" value={`~${clicks}`} />}
                {conv != null && <Stat label="Конверсій/міс" value={`~${conv}`} />}
                {cpa != null && <Stat label="CPA" value={`$${cpa}`} />}
            </div>
            {verdict && (
                <div className={`mt-5 text-sm ${color}`}>
                    <span className="font-mono uppercase tracking-wider mr-2">// {verdict}</span>
                    {verdictNote}
                </div>
            )}
        </section>
    );
};

const Stat = ({ label, value }) => (
    <div>
        <div className="text-xs text-faint-c uppercase font-mono">{label}</div>
        <div className="mt-1 font-mono font-bold text-2xl text-text-primary">{value}</div>
    </div>
);

const WarningsCard = ({ items }) => (
    <section className="mt-6 glass rounded-2xl p-5" style={{ borderColor: "rgba(251,191,36,0.3)" }} data-testid="strategy-warnings">
        <h3 className="font-display font-bold text-base text-warning-c inline-flex items-center gap-2">
            <AlertTriangle size={16} /> Зверніть увагу
        </h3>
        <ul className="mt-3 space-y-2">
            {items.map((w, i) => <li key={i} className="text-sm text-text-primary/90">{w}</li>)}
        </ul>
    </section>
);

const RecommendationsCard = ({ items }) => (
    <section className="mt-6 glass rounded-2xl p-5" data-testid="strategy-recommendations">
        <h3 className="font-display font-bold text-base text-text-primary inline-flex items-center gap-2">
            <Lightbulb size={16} className="text-primary-c" /> Додаткові рекомендації
        </h3>
        <ul className="mt-3 space-y-2">
            {items.map((r, i) => (
                <li key={i} className="flex gap-2 text-sm">
                    <span className="text-primary-c font-mono shrink-0">→</span>
                    <span className="text-text-primary/90">{r}</span>
                </li>
            ))}
        </ul>
    </section>
);

const CampaignCard = ({ data, idx, defaultOpen }) => {
    const [open, setOpen] = useState(!!defaultOpen);
    if (!data) return null;
    const langTarget = data.language_target || data.language;
    const bidding = data.bidding_strategy;
    const adGroupsCount = (data.ad_groups || []).length;
    const keywordsCount = (data.ad_groups || []).reduce((a, g) => a + (g.keywords || []).length, 0);

    return (
        <section className="glass rounded-2xl overflow-hidden" data-testid={`strategy-campaign-${idx}`}>
            <button
                onClick={() => setOpen(!open)}
                className="w-full p-6 flex items-center justify-between gap-3 text-left hover:bg-surface-hover/30 transition"
                data-testid={`campaign-toggle-${idx}`}
            >
                <div className="flex items-center gap-3 min-w-0">
                    {open ? <ChevronDown size={18} className="text-primary-c shrink-0" /> : <ChevronRight size={18} className="text-secondary-c shrink-0" />}
                    <div className="min-w-0">
                        <h2 className="font-display font-bold text-lg text-text-primary truncate">
                            Кампанія {idx + 1}: {data.name}
                        </h2>
                        <div className="mt-1 text-xs font-mono text-secondary-c flex flex-wrap items-center gap-2">
                            <span>{data.type || "Search"}</span>
                            <span className="text-faint-c">·</span>
                            <span>{adGroupsCount} груп</span>
                            <span className="text-faint-c">·</span>
                            <span>{keywordsCount} ключових слів</span>
                            {data.daily_budget_usd != null && <>
                                <span className="text-faint-c">·</span>
                                <span>${data.daily_budget_usd}/день</span>
                            </>}
                        </div>
                    </div>
                </div>
                <Copyable value={data.name} testid={`copy-campaign-name-${idx}`} />
            </button>

            {open && (
                <div className="px-7 pb-7 pt-1">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <Meta label="Тип" value={data.type} />
                        <Meta label="Гео" value={data.geo} />
                        <Meta label="Мова таргет." value={langTarget} />
                        {data.daily_budget_usd != null && <Meta label="Денний бюджет" value={`$${data.daily_budget_usd}`} />}
                    </div>
                    {bidding && (
                        <div className="mt-4 text-sm">
                            <span className="text-xs text-faint-c uppercase font-mono">Стратегія ставок:</span>{" "}
                            <span className="font-mono text-primary-c">{bidding}</span>
                            {data.bidding_note && <span className="ml-2 text-secondary-c">— {data.bidding_note}</span>}
                        </div>
                    )}

                    <div className="mt-6 space-y-5">
                        {(data.ad_groups || []).map((g, i) => <AdGroup key={i} data={g} idx={i} campIdx={idx} />)}
                    </div>

                    {data.extensions && <ExtensionsCard data={data.extensions} idx={idx} />}
                </div>
            )}
        </section>
    );
};

const Meta = ({ label, value }) => (
    <div>
        <div className="text-xs text-faint-c uppercase font-mono">{label}</div>
        <div className="mt-1 text-text-primary">{value || "—"}</div>
    </div>
);

const AdGroup = ({ data, idx, campIdx }) => {
    // Через хелпери adAssets — розуміють і legacy-об'єкти {text, match_type},
    // і готові рядки з multi-stage mapper. До цього чіпи ключових слів
    // рендерились порожніми, а розподіл завжди показував 100% phrase.
    const kwDetailed = getGroupKeywordsDetailed(data);
    const keywordsText = getGroupKeywords(data).join("\n");
    const negList = getGroupNegatives(data);
    const negKwText = negList.join("\n");
    const counts = getMatchTypeCounts(data);
    const total = counts.total;
    const pct = (n) => total ? Math.round((n / total) * 100) : 0;
    return (
        <div className="rounded-xl p-5 border border-border bg-surface-hover/40" data-testid={`ad-group-${campIdx}-${idx}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h3 className="font-display font-semibold text-base text-text-primary">{data.name}</h3>
                    {data.theme && <p className="mt-1 text-xs text-secondary-c">{data.theme}</p>}
                </div>
                <div className="flex items-center gap-2">
                    <Copyable value={keywordsText} testid={`copy-kw-${campIdx}-${idx}`} label="Ключові слова" />
                    {negKwText && <Copyable value={negKwText} testid={`copy-neg-${campIdx}-${idx}`} label="Мінус-слова" />}
                </div>
            </div>

            {/* Keyword stats bar: total + match-type breakdown */}
            {total > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] font-mono" data-testid={`kw-stats-${campIdx}-${idx}`}>
                    <span className="px-2 py-0.5 rounded border border-border text-secondary-c">всього {total}</span>
                    {counts.exact > 0 && (
                        <span className="px-2 py-0.5 rounded border border-success/30 bg-success/10 text-success-c">
                            [exact] {counts.exact} ({pct(counts.exact)}%)
                        </span>
                    )}
                    {counts.phrase > 0 && (
                        <span className="px-2 py-0.5 rounded border border-primary/30 bg-primary/10 text-primary-c">
                            "phrase" {counts.phrase} ({pct(counts.phrase)}%)
                        </span>
                    )}
                    {counts.broad > 0 && (
                        <span className="px-2 py-0.5 rounded border border-warning/30 bg-warning/10 text-warning-c">
                            broad {counts.broad} ({pct(counts.broad)}%)
                        </span>
                    )}
                    <span className="px-2 py-0.5 rounded border border-border text-faint-c">
                        мінус-слів: {negList.length}
                    </span>
                </div>
            )}

            {/* Keyword strategy explanation (narrative_language) */}
            {data.keyword_strategy_explanation && (
                <div
                    className="mt-3 rounded-lg border border-primary/20 bg-primary/5 p-3 text-xs text-text-secondary leading-relaxed"
                    data-testid={`kw-strategy-${campIdx}-${idx}`}
                >
                    <span className="font-semibold text-primary-c">Стратегія підбору:</span>{" "}
                    {data.keyword_strategy_explanation}
                </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
                {kwDetailed.map((k, j) => {
                    const variation = k.variation_type ? ` · ${k.variation_type}` : "";
                    return (
                        <span
                            key={j}
                            className="px-2.5 py-1 rounded-full text-xs font-mono border border-primary/30 bg-primary/10 text-text-primary"
                            title={`${k.match_type}${k.intent ? " · " + k.intent : ""}${variation}${k.priority ? " · priority " + k.priority : ""}`}
                        >
                            {k.display}
                        </span>
                    );
                })}
            </div>
            {negList.length > 0 && (
                <div className="mt-4">
                    <div className="text-xs text-faint-c uppercase font-mono">Мінус-слова</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                        {negList.map((nk, k) => (
                            <span key={k} className="px-2.5 py-1 rounded-full text-xs font-mono border border-border text-secondary-c">−{nk}</span>
                        ))}
                    </div>
                </div>
            )}
            {(() => {
                // Читаємо через getGroupAds — розуміє і legacy ads[], і
                // multi-stage headlines[]/descriptions[] прямо на ad_group.
                const groupAds = getGroupAds(data);
                if (groupAds.length === 0) return null;
                const single = groupAds.length === 1;
                return (
                    <div className="mt-6">
                        <div className="text-xs text-faint-c uppercase font-mono mb-3">Тексти оголошень</div>
                        <div className={`grid grid-cols-1 gap-3 ${single ? "" : "md:grid-cols-3"}`}>
                            {groupAds.map((ad, ai) => (
                                <AdVariant
                                    key={ad.variant || ai}
                                    ad={ad}
                                    campIdx={campIdx}
                                    groupIdx={idx}
                                />
                            ))}
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

const PART_LABEL = {
    keyword_match: "ключове",
    value_proposition: "вигода",
    cta_trust: "дія/довіра",
};

// Рендерить один рядок ассету з підсвіткою довжини та закріплення.
const AssetRow = ({ text, idx, kind, limit, pin, part, testid, label }) => {
    const st = lengthStatus(text, kind);
    const lenClass =
        st === "over" ? "text-error-c" : st === "long" ? "text-warning-c" : "text-faint-c";
    return (
        <div className="group/field">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] text-faint-c uppercase font-mono">{label}</span>
                <span className={`text-[10px] font-mono ${lenClass}`}>
                    {text.length}/{limit}
                </span>
                {pin && (
                    <span
                        className="text-[9px] font-mono px-1.5 py-0.5 rounded border border-border text-primary-c"
                        title={`Закріплено на позицію ${pin}`}
                    >
                        📌 поз. {pin}
                    </span>
                )}
                {part && PART_LABEL[part] && (
                    <span className="text-[9px] font-mono text-faint-c">{PART_LABEL[part]}</span>
                )}
            </div>
            <div className="mt-0.5 flex items-start gap-2">
                <div className="flex-1 text-sm text-text-primary break-words">{text}</div>
                {text && <Copyable value={text} testid={testid} label={`Скопіювати ${label}`} iconOnly />}
            </div>
        </div>
    );
};

const AdVariant = ({ ad, campIdx, groupIdx }) => {
    const headlines = getAdHeadlines(ad);
    const descriptions = getAdDescriptions(ad);
    const pins = getHeadlinePins(ad);
    const parts = getHeadlineParts(ad);
    const pinnedCount = countPinned(ad);
    const isRsa = ad?._synthesized || Array.isArray(ad?.headlines);
    const key = ad?.variant || "RSA";

    return (
        <div
            className="rounded-lg p-4 border border-border bg-surface"
            data-testid={`ad-${campIdx}-${groupIdx}-${key}`}
        >
            <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-xs text-primary-c">
                    {isRsa ? `RSA — ${headlines.length}/${RSA_MAX_HEADLINES} заголовків` : `Варіант ${key}`}
                </span>
                <Copyable
                    value={adAsPlainText(ad)}
                    testid={`copy-ad-${campIdx}-${groupIdx}-${key}`}
                    label="Все"
                />
            </div>
            {ad?.focus && <div className="mt-1 text-[10px] text-faint-c">{ad.focus}</div>}
            {isRsa && pinnedCount > 0 && (
                <div className="mt-1 text-[10px] text-faint-c">
                    Закріплено {pinnedCount} з {headlines.length} — решту Google міксує сам
                </div>
            )}

            <div className="mt-3 space-y-2.5">
                {headlines.map((text, i) => (
                    <AssetRow
                        key={`h${i}`}
                        text={text}
                        idx={i}
                        kind="headline"
                        limit={LIMIT_HEADLINE}
                        pin={pins[i]}
                        part={parts[i]}
                        label={`заголовок ${i + 1}`}
                        testid={`copy-headline-${i + 1}-${campIdx}-${groupIdx}-${key}`}
                    />
                ))}
                {descriptions.map((text, i) => (
                    <AssetRow
                        key={`d${i}`}
                        text={text}
                        idx={i}
                        kind="description"
                        limit={LIMIT_DESCRIPTION}
                        label={`опис ${i + 1}`}
                        testid={`copy-description-${i + 1}-${campIdx}-${groupIdx}-${key}`}
                    />
                ))}
                {headlines.length === 0 && descriptions.length === 0 && (
                    <div className="text-xs text-faint-c">Текстів оголошень немає.</div>
                )}
            </div>
        </div>
    );
};

const ExtensionsCard = ({ data, idx }) => {
    const callouts = data.callouts || [];
    const sitelinks = data.sitelinks || [];
    if (!callouts.length && !sitelinks.length && !data.call_extension_recommended) return null;
    return (
        <div className="mt-6 rounded-xl p-5 border border-border bg-surface-hover/40" data-testid={`extensions-${idx}`}>
            <h3 className="font-display font-semibold text-base text-text-primary">Розширення оголошень</h3>
            {callouts.length > 0 && (
                <div className="mt-4">
                    <div className="text-xs text-faint-c uppercase font-mono">Уточнення (callouts)</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                        {callouts.map((c, i) => (
                            <span key={i} className="px-2.5 py-1 rounded-full text-xs border border-primary/30 bg-primary/10 text-text-primary">{c}</span>
                        ))}
                    </div>
                </div>
            )}
            {sitelinks.length > 0 && (
                <div className="mt-4">
                    <div className="text-xs text-faint-c uppercase font-mono">Sitelinks</div>
                    <ul className="mt-2 space-y-1.5" data-testid={`sitelinks-list-${idx}`}>
                        {sitelinks.map((s, i) => (
                            <li key={i} className="text-sm">
                                <span className="text-text-primary">{s.text}</span>
                                {s.url && <span className="ml-2 font-mono text-xs text-secondary-c">→ {s.url}</span>}
                            </li>
                        ))}
                    </ul>
                    <div
                        className="mt-3 rounded-lg border border-warning/30 bg-warning/10 p-3 text-xs text-text-secondary leading-relaxed"
                        data-testid={`sitelinks-note-${idx}`}
                    >
                        <span className="font-semibold text-text-primary">Примітка:</span>{" "}
                        Показані тут посилання — лише приклади структури. Замініть їх на реальні URL зі свого сайту:
                        зазвичай це сторінки <span className="font-mono">/contacts</span>, <span className="font-mono">/about</span>,
                        <span className="font-mono"> /services</span>, <span className="font-mono">/portfolio</span>, окремих послуг або акцій.
                        Знайти їх можна в меню вашого сайту або в Google: введіть <span className="font-mono">site:yourdomain.com</span>,
                        щоб побачити всі проіндексовані сторінки. Кожен sitelink має вести на конкретну, релевантну сторінку (не на головну).
                    </div>
                </div>
            )}
            {data.call_extension_recommended && (
                <div className="mt-4 text-sm text-secondary-c">
                    Рекомендуємо увімкнути розширення дзвінків у Google Ads
                </div>
            )}
        </div>
    );
};

// PR: delayed-paywall. Paywall card shown at the start of Step 3 for users
// without a plan. Picks the recommended tier based on their profile (always
// "growth" for now) and routes them to Pricing with `pending_action=generate_strategy`
// in the query so CheckoutSuccess knows to come back here and auto-resume.
const Step3Paywall = ({ navigate }) => {
    const goToPricing = () => {
        // We persist the pending action via /checkout/session payload, but
        // also pass it in the URL so the Pricing page can show a context
        // banner ("Ще один крок — оберіть план") and pre-select Growth.
        navigate("/pricing?next=generate_strategy");
    };

    return (
        <OnboardingShell current={3}>
            <div className="max-w-[640px] mx-auto py-12 text-center" data-testid="onb3-paywall">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs font-mono text-secondary-c">
                    <Wand2 size={12} className="text-primary-c" />
                    Останній крок
                </div>
                <h1
                    className="mt-5 font-display font-extrabold text-text-primary"
                    style={{ fontSize: "clamp(28px, 4vw, 40px)", letterSpacing: "-0.02em" }}
                >
                    Ваша стратегія готова до генерації
                </h1>
                <p className="mt-4 text-secondary-c text-lg">
                    AI підготує структуру кампаній, 30–50 ключових слів на групу, тексти оголошень і бюджети. Оберіть план — і ми все згенеруємо за ~2 хвилини.
                </p>

                <ul className="mt-8 inline-flex flex-col items-start gap-2.5 text-sm text-text-primary">
                    {[
                        "Повна стратегія Google Ads під ваш бізнес",
                        "AI-рекомендації по оптимізації щотижня",
                        "Аналіз CSV-звітів з бенчмарками",
                        "Можна скасувати в будь-який момент",
                    ].map((t, i) => (
                        <li key={i} className="flex items-start gap-2">
                            <Check size={16} className="mt-0.5 text-success-c shrink-0" />
                            {t}
                        </li>
                    ))}
                </ul>

                <button
                    onClick={goToPricing}
                    className="mt-10 btn-primary text-base px-8 py-3"
                    data-testid="onb3-paywall-cta"
                >
                    Обрати план і згенерувати
                    <ArrowRight size={16} className="ml-2 inline" />
                </button>

                <p className="mt-4 text-xs text-faint-c font-mono">
                    Безпечна оплата через Stripe • повернення коштів протягом 7 днів
                </p>
            </div>
        </OnboardingShell>
    );
};

